<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { CopyPlus, FilePenLine, FilePlus2, Printer } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { formatDisplayDate } from '@yuksalish/ui'
import PaginationControls from '../../components/PaginationControls.vue'
import SockPassportDrawingPreview from '../components/SockPassportDrawingPreview.vue'
import { resolveAssetUrl } from '../productPresentation'
import {
  getBomVersion,
  getTechnicalPassport,
  getTechnicalPassportMaterialRequirements,
  listPersonnelEmployees,
  listProductBomVersions,
  listTechnicalPassports,
  duplicateTechnicalPassport,
} from '../productService'
import { diagnosePassportConsistency } from '../passportConsistency'
import { applyBomToPassport } from '../bomPassportLink'
import { restoreDecorativeElement } from '../sockDecorations'
import type {
  BomVersion,
  ProductionEmployee,
  TechnicalPassport,
  TechnicalPassportMaterialRequirements,
} from '../types'

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()
const passport = ref<TechnicalPassport | null>(null)
const approvalPeople = ref<ProductionEmployee[]>([])
const relatedPassports = ref<TechnicalPassport[]>([])
const materialRequirements = ref<TechnicalPassportMaterialRequirements | null>(null)
const pairQuantity = ref(1000)
const loading = ref(false)
const calculating = ref(false)
const errorMessage = ref('')
const calculationError = ref('')
const relatedError = ref('')
const consistencyError = ref('')
const activeBom = ref<BomVersion | null>(null)
const revisionError = ref('')
const creatingRevision = ref(false)
const routePage = ref(1)
const routePageSize = ref(5)
const materialPage = ref(1)
const materialPageSize = ref(5)
const requirementPage = ref(1)
const requirementPageSize = ref(5)
const revisionPage = ref(1)
const revisionPageSize = ref(5)
const passportId = computed(() => Number(route.params.id))
const hasValidId = computed(
  () => Number.isInteger(passportId.value) && passportId.value > 0,
)
const canManage = computed(
  () => auth.user?.permissions.includes('production_orders.manage') ?? false,
)
const canViewBom = computed(
  () => auth.user?.permissions.includes('bom.view') ?? false,
)
const consistency = computed(() =>
  passport.value && canViewBom.value
    ? diagnosePassportConsistency(activeBom.value, passport.value)
    : null,
)
const latestRevision = computed(() =>
  passport.value?.revisions?.at(-1),
)
const displayElements = computed(() =>
  (passport.value?.elements ?? []).map((element) => restoreDecorativeElement(element)),
)
const mainProductImageUrl = computed(() =>
  resolveAssetUrl(passport.value?.main_product_image ?? null),
)
const technicalDrawingImageUrl = computed(() =>
  resolveAssetUrl(passport.value?.technical_drawing_image ?? null),
)
const resolvedAttachments = computed(() =>
  (passport.value?.attachments ?? []).map((attachment, index) => ({
    key: attachment.id ?? `${attachment.attachment_type || 'attachment'}-${index}`,
    type: attachment.attachment_type || 'other',
    fileName: attachment.file_name || attachment.file_url || `attachment-${index + 1}`,
    fileUrl: resolveAssetUrl(attachment.file_url ?? null),
    notes: attachment.notes || '',
  })),
)
const approvalSteps = computed(() => {
  if (!passport.value) return []

  const selectedPersonnelName = (id?: number | string | null) => {
    if (id == null) return ''
    const employee = approvalPeople.value.find(
      (person) => Number(person.user_id ?? person.id) === Number(id),
    )
    return employee
      ? `${employee.first_name} ${employee.last_name}`.trim()
      : ''
  }

  return [
    {
      key: 'technologist',
      role: t('engineering.passportDetail.technologist'),
      approver: passport.value.technologist_id != null
        ? (selectedPersonnelName(passport.value.technologist_id) || `#${passport.value.technologist_id}`)
        : t('engineering.passportDetail.notAssigned'),
      approvedAt: formatDisplayDate(passport.value.technologist_approved_at) || '',
    },
    {
      key: 'shift-manager',
      role: t('engineering.passportDetail.shiftManager'),
      approver: passport.value.shift_manager_id != null
        ? (selectedPersonnelName(passport.value.shift_manager_id) || `#${passport.value.shift_manager_id}`)
        : t('engineering.passportDetail.notAssigned'),
      approvedAt: formatDisplayDate(passport.value.shift_manager_approved_at) || '',
    },
    {
      key: 'manager',
      role: t('engineering.passportDetail.manager'),
      approver: passport.value.manager_id != null
        ? (selectedPersonnelName(passport.value.manager_id) || `#${passport.value.manager_id}`)
        : t('engineering.passportDetail.notAssigned'),
      approvedAt: formatDisplayDate(passport.value.manager_approved_at) || '',
    },
  ]
})

function printPassport(): void {
  window.print()
}

async function loadConsistency(productId: number): Promise<void> {
  if (!canViewBom.value) return
  consistencyError.value = ''
  try {
    const versions = await listProductBomVersions(client, productId)
    const active = versions.find((version) => Boolean(version.is_active))
    activeBom.value = active ? await getBomVersion(client, active.id) : null
    if (activeBom.value && passport.value) {
      const linked = applyBomToPassport(activeBom.value)
      passport.value.routes = linked.routes
      passport.value.raw_materials = linked.materials
    }
  } catch (error: unknown) {
    activeBom.value = null
    consistencyError.value = formatApiError(error)
  }
}

async function createRevision(): Promise<void> {
  if (!passport.value) return
  creatingRevision.value = true
  revisionError.value = ''
  try {
    const result = await duplicateTechnicalPassport(
      client,
      passport.value.id,
      passport.value.product_id,
    )
    await router.push(`/technical-passports/${result.id}/edit`)
  } catch (error: unknown) {
    revisionError.value = formatApiError(error)
  } finally {
    creatingRevision.value = false
  }
}

const sectionCounts = computed(() => {
  if (!passport.value) {
    return []
  }
  return [
    [t('engineering.passportForm.measurements'), passport.value.measurements?.length ?? 0],
    [t('engineering.passportForm.sockElements'), passport.value.elements?.length ?? 0],
    [t('engineering.passportForm.yarns'), passport.value.yarns?.length ?? 0],
    [t('engineering.passportForm.materials'), passport.value.raw_materials?.length ?? 0],
    [t('engineering.passportForm.routes'), passport.value.routes?.length ?? 0],
    [t('engineering.passportForm.qcChecks'), passport.value.qc_items?.length ?? 0],
    [t('engineering.passportDetail.machines'), passport.value.machines?.length ?? 0],
    [t('engineering.passportDetail.packaging'), passport.value.packaging?.length ?? 0],
  ]
})
function paginate<T>(items: T[] | undefined, page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize
  return (items ?? []).slice(start, start + pageSize)
}
const paginatedRoutes = computed(() =>
  paginate(passport.value?.routes, routePage.value, routePageSize.value),
)
const paginatedMaterials = computed(() =>
  paginate(
    passport.value?.raw_materials,
    materialPage.value,
    materialPageSize.value,
  ),
)
const paginatedRequirements = computed(() =>
  paginate(
    materialRequirements.value?.materials,
    requirementPage.value,
    requirementPageSize.value,
  ),
)
const paginatedRevisions = computed(() =>
  paginate(
    passport.value?.revisions,
    revisionPage.value,
    revisionPageSize.value,
  ),
)

function clampPage(page: number, pageSize: number, total: number): number {
  return Math.min(page, Math.max(1, Math.ceil(total / pageSize)))
}

async function loadPassport(): Promise<void> {
  if (!hasValidId.value) {
    passport.value = null
    errorMessage.value = t('engineering.passportDetail.invalidId')
    return
  }
  loading.value = true
  errorMessage.value = ''
  try {
    passport.value = await getTechnicalPassport(client, passportId.value)
    try {
      approvalPeople.value = await listPersonnelEmployees(client)
    } catch {
      approvalPeople.value = []
    }
    await loadConsistency(passport.value.product_id)
    routePage.value = 1
    materialPage.value = 1
    revisionPage.value = 1
    materialRequirements.value = null
    calculationError.value = ''
    relatedError.value = ''
    try {
      relatedPassports.value = await listTechnicalPassports(
        client,
        passport.value.product_id,
      )
    } catch (error: unknown) {
      relatedPassports.value = []
      relatedError.value = formatApiError(error)
    }
  } catch (error: unknown) {
    passport.value = null
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function calculateRequirements(): Promise<void> {
  const pairs = Number(pairQuantity.value)
  if (!Number.isFinite(pairs) || pairs <= 0) {
    calculationError.value = 'Enter a quantity greater than zero.'
    materialRequirements.value = null
    return
  }

  calculating.value = true
  calculationError.value = ''
  try {
    materialRequirements.value =
      await getTechnicalPassportMaterialRequirements(
        client,
        passportId.value,
        pairs,
      )
    requirementPage.value = 1
  } catch (error: unknown) {
    materialRequirements.value = null
    calculationError.value = formatApiError(error)
  } finally {
    calculating.value = false
  }
}

onMounted(loadPassport)
watch(passportId, loadPassport)
watch([() => passport.value?.routes?.length, routePageSize], () => {
  routePage.value = clampPage(
    routePage.value,
    routePageSize.value,
    passport.value?.routes?.length ?? 0,
  )
})
watch([() => passport.value?.raw_materials?.length, materialPageSize], () => {
  materialPage.value = clampPage(
    materialPage.value,
    materialPageSize.value,
    passport.value?.raw_materials?.length ?? 0,
  )
})
watch([
  () => materialRequirements.value?.materials.length,
  requirementPageSize,
], () => {
  requirementPage.value = clampPage(
    requirementPage.value,
    requirementPageSize.value,
    materialRequirements.value?.materials.length ?? 0,
  )
})
watch([() => passport.value?.revisions?.length, revisionPageSize], () => {
  revisionPage.value = clampPage(
    revisionPage.value,
    revisionPageSize.value,
    passport.value?.revisions?.length ?? 0,
  )
})
</script>

<template>
  <div class="page-toolbar">
    <RouterLink
      class="back-link"
      :data-tooltip="$t('engineering.passportDetail.back')"
      :to="passport ? `/products/${passport.product_id}` : '/'"
    >
      {{ $t('engineering.passportDetail.back') }}
    </RouterLink>
    <div v-if="passport" class="page-toolbar__actions">
      <RouterLink
        v-if="canManage && passport.status !== 'approved'"
        class="author-action"
        :to="`/technical-passports/${passport.id}/edit`"
      ><FilePenLine :size="16" aria-hidden="true" />{{ $t('engineering.passportDetail.edit') }}</RouterLink>
      <button
        v-if="canManage && passport.status === 'approved'"
        class="author-action"
        type="button"
        :disabled="creatingRevision"
        @click="createRevision"
      ><CopyPlus :size="16" aria-hidden="true" />{{ creatingRevision ? $t('engineering.passportDetail.creating') : $t('engineering.passportDetail.newRevision') }}</button>
      <RouterLink
        v-if="canManage && passport.status === 'approved'"
        class="author-action author-action--primary"
        :to="`/production-orders/new-guided?technical_passport_id=${passport.id}`"
      ><FilePlus2 :size="16" aria-hidden="true" />{{ $t('engineering.passportDetail.prepareOrder') }}</RouterLink>
      <button class="author-action print-action" type="button" @click="printPassport">
        <Printer :size="16" aria-hidden="true" />{{ $t('engineering.passportDetail.print') }}
      </button>
    </div>
  </div>

  <div v-if="loading" class="page-state">{{ $t('engineering.passportDetail.loading') }}</div>
  <div v-else-if="errorMessage" class="page-state page-state--error">
    <p role="alert">{{ errorMessage }}</p>
    <button v-if="hasValidId" type="button" @click="loadPassport">{{ $t('engineering.action.retry') }}</button>
  </div>

  <template v-else-if="passport">
    <div class="page-heading">
      <div>
        <p class="eyebrow">{{ $t('engineering.passportDetail.eyebrow', { id: passport.id }) }}</p>
        <h2>{{ passport.product_name || $t('engineering.passportDetail.product', { id: passport.product_id }) }}</h2>
        <p>{{ passport.description || $t('engineering.passportDetail.approvedDefinition') }}</p>
      </div>
      <div class="status-stack">
        <span class="status">{{ $t(`engineering.passport.status.${passport.status}`) }}</span>
        <span
          :class="passport.production_readiness?.is_ready
            ? 'readiness--ready'
            : 'readiness--blocked'"
        >
          {{ passport.production_readiness?.is_ready ? $t('engineering.catalog.ready') : $t('engineering.catalog.notReady') }}
        </span>
      </div>
    </div>
    <p v-if="revisionError" class="section-error" role="alert">{{ revisionError }}</p>

    <section class="print-document" :aria-label="$t('engineering.passportDetail.printableAria')">
      <header class="print-header">
        <div>
          <p>{{ $t('engineering.passportDetail.brand') }}</p>
          <h1>{{ $t('engineering.passportDetail.title') }}</h1>
          <strong>{{ passport.product_name || $t('engineering.passportDetail.product', { id: passport.product_id }) }}</strong>
        </div>
        <dl>
          <div><dt>{{ $t('engineering.passportDetail.passport') }}</dt><dd>#{{ passport.id }}</dd></div>
          <div><dt>{{ $t('engineering.passportDetail.revision') }}</dt><dd>{{ latestRevision?.revision_no || latestRevision?.revision || '1' }}</dd></div>
          <div><dt>{{ $t('engineering.passportDetail.status') }}</dt><dd>{{ $t(`engineering.passport.status.${passport.status}`) }}</dd></div>
          <div><dt>{{ $t('engineering.passportDetail.date') }}</dt><dd>{{ formatDisplayDate(passport.updated_at || passport.created_at) || '—' }}</dd></div>
        </dl>
      </header>

      <div class="print-definition">
        <div><span>{{ $t('engineering.passportDetail.productType') }}</span><strong>{{ passport.product_type || '—' }}</strong></div>
        <div><span>{{ $t('engineering.passportDetail.template') }}</span><strong>{{ passport.template_type || '—' }}</strong></div>
        <div><span>{{ $t('engineering.passportDetail.sockType') }}</span><strong>{{ passport.sock_type || '—' }}</strong></div>
        <div><span>{{ $t('engineering.passportDetail.sizeRange') }}</span><strong>{{ passport.size_range || '—' }}</strong></div>
        <div><span>{{ $t('engineering.passportDetail.season') }}</span><strong>{{ passport.season || '—' }}</strong></div>
        <div><span>{{ $t('engineering.passportDetail.pairWeight') }}</span><strong>{{ passport.sock_weight_gram ?? '—' }} g</strong></div>
      </div>

      <SockPassportDrawingPreview
        :dimensions="passport.dimensions"
        :elements="displayElements"
        :yarns="passport.yarns"
        :sock-type="passport.sock_type"
      />
      <div v-if="resolvedAttachments.length" class="print-columns print-columns--media">
        <div v-if="resolvedAttachments.length" class="print-media-block">
          <h2>{{ $t('engineering.passportForm.attachments') }}</h2>
          <ul class="print-attachment-list">
            <li v-for="attachment in resolvedAttachments" :key="attachment.key">
              <strong>{{ $t(`engineering.passportForm.${attachment.type}`) }}</strong>
              <span>{{ attachment.fileName }}</span>
              <small>{{ attachment.fileUrl || '—' }}</small>
            </li>
          </ul>
        </div>
      </div>

      <div class="print-columns">
        <div>
          <h2>{{ $t('engineering.passportDetail.machines') }}</h2>
          <table>
            <thead><tr><th>{{ $t('engineering.passportDetail.machine') }}</th><th>{{ $t('engineering.passportDetail.needles') }}</th><th>{{ $t('engineering.passportDetail.compatible') }}</th></tr></thead>
            <tbody>
              <tr v-for="(machine, index) in passport.machines ?? []" :key="machine.id ?? index">
                <td>{{ machine.machine_name || $t('engineering.passportDetail.equipment', { id: machine.equipment_reference_id }) }}</td>
                <td>{{ machine.needle_count ?? '—' }}</td>
                <td>{{ machine.is_compatible ? $t('engineering.passportDetail.yes') : $t('engineering.passportDetail.no') }}</td>
              </tr>
              <tr v-if="!passport.machines?.length"><td colspan="3">{{ $t('engineering.passportDetail.noMachines') }}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h2>{{ $t('engineering.passportDetail.packaging') }}</h2>
          <table>
            <thead><tr><th>{{ $t('engineering.passportDetail.item') }}</th><th>{{ $t('engineering.passportDetail.quantity') }}</th></tr></thead>
            <tbody>
              <tr v-for="(item, index) in passport.packaging ?? []" :key="item.id ?? index">
                <td>{{ item.packaging_name || $t('engineering.passportDetail.packagingItem', { id: item.packaging_product_id || item.packaging_reference_id }) }}</td>
                <td>{{ item.quantity ?? '—' }}</td>
              </tr>
              <tr v-if="!passport.packaging?.length"><td colspan="2">{{ $t('engineering.passportDetail.noPackaging') }}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h2>{{ $t('engineering.passportDetail.labels') }}</h2>
          <table>
            <thead><tr><th>{{ $t('engineering.passportForm.labelName') }}</th><th>{{ $t('engineering.passportForm.labelCode') }}</th><th>{{ $t('engineering.passportDetail.compatible') }}</th></tr></thead>
            <tbody>
              <tr v-for="(label, index) in passport.labels ?? []" :key="label.id ?? index">
                <td>{{ label.label_name || '—' }}</td>
                <td>{{ label.label_code || '—' }}</td>
                <td>{{ label.is_required ? $t('engineering.passportDetail.yes') : $t('engineering.passportDetail.no') }}</td>
              </tr>
              <tr v-if="!passport.labels?.length"><td colspan="3">{{ $t('engineering.passportDetail.noLabels') }}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>{{ $t('engineering.passportDetail.rawMaterials') }}</h2>
      <table>
        <thead><tr><th>{{ $t('engineering.passportDetail.material') }}</th><th>{{ $t('engineering.passportDetail.stage') }}</th><th>{{ $t('engineering.passportDetail.perPair') }}</th><th>{{ $t('engineering.passportDetail.waste') }}</th></tr></thead>
        <tbody>
          <tr v-for="(material, index) in passport.raw_materials ?? []" :key="material.id ?? index">
            <td>{{ material.material_name_snapshot || $t('engineering.passportDetail.materialItem', { id: material.material_product_id }) }}</td>
            <td>{{ material.stage_code || '—' }}</td>
            <td>{{ material.consumption_per_pair ?? 0 }} {{ material.uom || '' }}</td>
            <td>{{ material.waste_percent ?? 0 }}%</td>
          </tr>
          <tr v-if="!passport.raw_materials?.length"><td colspan="4">{{ $t('engineering.passportDetail.noMaterials') }}</td></tr>
        </tbody>
      </table>

      <h2>{{ $t('engineering.passportDetail.productionRoute') }}</h2>
      <table>
        <thead><tr><th>{{ $t('engineering.passportDetail.step') }}</th><th>{{ $t('engineering.passportDetail.operation') }}</th><th>{{ $t('engineering.passportDetail.workstation') }}</th><th>{{ $t('engineering.passportDetail.time') }}</th><th>{{ $t('engineering.passportDetail.qc') }}</th></tr></thead>
        <tbody>
          <tr v-for="(step, index) in passport.routes ?? []" :key="step.id ?? index">
            <td>{{ step.sort_order ?? index + 1 }}</td>
            <td>{{ step.operation_name || '—' }}</td>
            <td>{{ step.workstation_name || '—' }}</td>
            <td>{{ $t('engineering.passportDetail.minutes', { count: step.standard_time_minutes ?? 0 }) }}</td>
            <td>{{ step.qc_required ? $t('engineering.passportDetail.yes') : $t('engineering.passportDetail.no') }}</td>
          </tr>
          <tr v-if="!passport.routes?.length"><td colspan="5">{{ $t('engineering.passportDetail.noRoute') }}</td></tr>
        </tbody>
      </table>

      <h2>{{ $t('engineering.passportDetail.quality') }}</h2>
      <table>
        <thead><tr><th>{{ $t('engineering.passportDetail.stage') }}</th><th>{{ $t('engineering.passportDetail.check') }}</th><th>{{ $t('engineering.passportDetail.acceptable') }}</th><th>{{ $t('engineering.passportDetail.method') }}</th></tr></thead>
        <tbody>
          <tr v-for="(item, index) in passport.qc_items ?? []" :key="item.id ?? index">
            <td>{{ item.stage || '—' }}</td>
            <td>{{ item.item_name || '—' }}</td>
            <td>{{ item.acceptable_value || '—' }}</td>
            <td>{{ item.inspection_method || '—' }}</td>
          </tr>
          <tr v-if="!passport.qc_items?.length"><td colspan="4">{{ $t('engineering.passportDetail.noQuality') }}</td></tr>
        </tbody>
      </table>

      <footer class="print-approvals">
        <div><span>{{ $t('engineering.passportDetail.preparedBy') }}</span><strong>{{ passport.prepared_by || '—' }}</strong><i>{{ $t('engineering.passportDetail.signature') }}</i></div>
        <div><span>{{ $t('engineering.passportDetail.checkedBy') }}</span><strong>{{ latestRevision?.changed_by || latestRevision?.by || '—' }}</strong><i>{{ $t('engineering.passportDetail.signature') }}</i></div>
        <div><span>{{ $t('engineering.passportDetail.approved') }}</span><strong>{{ passport.status === 'approved' ? $t('engineering.passportDetail.approved') : $t('engineering.passportDetail.pending') }}</strong><i>{{ $t('engineering.passportDetail.dateSignature') }}</i></div>
      </footer>
    </section>

    <section class="consistency-section">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.consistency.title') }}</h3>
          <p>{{ $t('engineering.consistency.help') }}</p>
        </div>
        <span
          v-if="consistency"
          class="consistency-status"
          :class="consistency.isConsistent ? 'consistency-status--ok' : 'consistency-status--issue'"
        >
          {{ consistency.isConsistent ? $t('engineering.consistency.consistent') : $t('engineering.consistency.issues', { count: consistency.issues.length }) }}
        </span>
      </div>
      <div v-if="!canViewBom" class="section-state">
        {{ $t('engineering.consistency.permission', { permission: 'bom.view' }) }}
      </div>
      <div v-else-if="consistencyError" class="section-error" role="alert">
        {{ consistencyError }}
      </div>
      <div v-else-if="consistency?.isConsistent" class="consistency-ok">
        {{ $t('engineering.consistency.matched') }}
      </div>
      <ul v-else-if="consistency" class="consistency-list">
        <li
          v-for="issue in consistency.issues"
          :key="`${issue.code}-${issue.message}`"
          :class="`consistency-list--${issue.severity}`"
        >
          <strong>{{ issue.severity === 'error' ? $t('engineering.consistency.mismatch') : $t('engineering.consistency.review') }}</strong>
          <span>{{ issue.message }}</span>
        </li>
      </ul>
    </section>

    <section>
      <h3>{{ $t('engineering.passportDetail.definition') }}</h3>
      <dl class="definition-grid">
        <div><dt>{{ $t('engineering.passportDetail.template') }}</dt><dd>{{ passport.template_type || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.productType') }}</dt><dd>{{ passport.product_type || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.sockType') }}</dt><dd>{{ passport.sock_type || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.season') }}</dt><dd>{{ passport.season || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.sizeRange') }}</dt><dd>{{ passport.size_range || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.weight') }}</dt><dd>{{ passport.sock_weight_gram ? `${passport.sock_weight_gram} g` : "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.machineType') }}</dt><dd>{{ passport.machine_type || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.needles') }}</dt><dd>{{ passport.machine_needle_count || "-" }}</dd></div>
        <div><dt>{{ $t('engineering.passportDetail.preparedBy') }}</dt><dd>{{ passport.prepared_by || "-" }}</dd></div>
      </dl>
    </section>

    <section v-if="resolvedAttachments.length">
      <div class="section-heading">
        <h3>{{ $t('engineering.passportForm.attachments') }}</h3>
        <span>{{ resolvedAttachments.length }}</span>
      </div>
      <div v-if="resolvedAttachments.length" class="attachment-list">
        <article v-for="attachment in resolvedAttachments" :key="attachment.key" class="attachment-list__item">
          <div>
            <strong>{{ $t(`engineering.passportForm.${attachment.type}`) }}</strong>
            <p>{{ attachment.fileName }}</p>
            <small v-if="attachment.notes">{{ attachment.notes }}</small>
          </div>
          <a
            v-if="attachment.fileUrl"
            class="attachment-link"
            :href="attachment.fileUrl"
            target="_blank"
            rel="noreferrer"
          >
            {{ $t('engineering.passportForm.openFile') }}
          </a>
        </article>
      </div>
    </section>

    <section>
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.passportDetail.approval') }}</h3>
          <p>{{ $t('engineering.passportDetail.approvalHelp') }}</p>
        </div>
        <span class="consistency-status" :class="passport.status === 'approved' ? 'consistency-status--ok' : 'consistency-status--issue'">
          {{ $t(`engineering.passport.status.${passport.status}`) }}
        </span>
      </div>
      <div v-if="passport.status === 'rejected'" class="rejection-banner" role="alert">
        <strong>{{ $t('engineering.passportDetail.rejectedTitle') }}</strong>
        <p>{{ passport.reject_reason || $t('engineering.passportDetail.rejectedNoReason') }}</p>
      </div>
      <div class="approval-grid">
        <article v-for="step in approvalSteps" :key="step.key" class="approval-card">
          <div class="approval-card__head">
            <strong>{{ step.role }}</strong>
            <span :class="step.approvedAt ? 'readiness--ready' : 'readiness--blocked'">
              {{ step.approvedAt ? $t('engineering.passportDetail.approved') : $t('engineering.passportDetail.pending') }}
            </span>
          </div>
          <dl>
            <div>
              <dt>{{ $t('engineering.passportDetail.approver') }}</dt>
              <dd>{{ step.approver }}</dd>
            </div>
            <div>
              <dt>{{ $t('engineering.passportDetail.approvedAt') }}</dt>
              <dd>{{ step.approvedAt || $t('engineering.passportDetail.notApproved') }}</dd>
            </div>
          </dl>
        </article>
      </div>
    </section>

    <section>
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.passportDetail.records') }}</h3>
          <p>{{ $t('engineering.passportDetail.recordsHelp') }}</p>
        </div>
        <span>{{ $t('engineering.passportDetail.recordCount', { count: relatedPassports.length }) }}</span>
      </div>
      <div v-if="relatedError" class="inline-error" role="alert">
        {{ relatedError }}
      </div>
      <div v-else class="passport-tabs">
        <RouterLink
          v-for="item in relatedPassports"
          :key="item.id"
          :class="{ active: item.id === passport.id }"
          :to="`/technical-passports/${item.id}`"
        >
          #{{ item.id }} · {{ $t(`engineering.passport.status.${item.status}`) }}
        </RouterLink>
      </div>
    </section>

    <section class="coverage-band">
      <div v-for="[label, count] in sectionCounts" :key="String(label)">
        <strong>{{ count }}</strong>
        <span>{{ label }}</span>
      </div>
    </section>

    <section v-if="passport.dimensions || displayElements.length || passport.yarns?.length" class="structure-section">
      <div class="section-heading structure-heading">
        <div><p class="section-kicker">{{ $t('engineering.structurePreview.specification') }}</p><h3>{{ $t('engineering.passportDetail.sockStructure') }}</h3><p>{{ $t('engineering.passportDetail.sockStructureHelp') }}</p></div>
        <div class="structure-summary">
          <span>{{ $t('engineering.structurePreview.featureCount', { count: displayElements.length }) }}</span>
          <span>{{ $t('engineering.structurePreview.yarnCount', { count: passport.yarns?.length ?? 0 }) }}</span>
          <span>{{ $t('engineering.structurePreview.requiredCount', { count: displayElements.filter((element) => Boolean(element.is_required)).length }) }}</span>
        </div>
      </div>
      <div class="structure-main">
      <SockPassportDrawingPreview
        :dimensions="passport.dimensions"
        :elements="displayElements"
        :yarns="passport.yarns"
        :sock-type="passport.sock_type"
      />
      <div v-if="passport.dimensions" class="dimension-panel">
        <h4>{{ $t('engineering.structurePreview.dimensions') }}</h4>
      <dl class="structure-dimensions">
        <div v-for="(value, key) in passport.dimensions" :key="key">
          <dt>{{ $t(`engineering.structurePreview.dimension.${String(key)}`) }}</dt>
          <dd><strong>{{ value ?? '—' }}</strong><span>mm</span></dd>
        </div>
      </dl>
      </div>
      </div>
      <div class="structure-secondary">
      <div v-if="displayElements.length" class="structure-list">
        <h4>{{ $t('engineering.structurePreview.elements') }}</h4>
        <article v-for="(element, index) in displayElements" :key="element.id ?? index">
          <div class="feature-index">{{ index + 1 }}</div>
          <div class="feature-copy">
            <div>
              <strong>{{ element.element_type || $t('engineering.passportDetail.element') }}</strong>
              <span :class="element.is_required ? 'feature-status--required' : 'feature-status--optional'">{{ element.is_required ? $t('engineering.structurePreview.required') : $t('engineering.structurePreview.optional') }}</span>
            </div>
            <p>{{ element.position_code || $t('engineering.passportDetail.noPosition') }} · {{ element.knit_type || $t('engineering.passportDetail.noKnitType') }}</p>
          </div>
          <div class="feature-size"><strong>{{ element.length_mm ?? '—' }} × {{ element.width_mm ?? '—' }} × {{ element.height_mm ?? '—' }}</strong><span>mm</span></div>
        </article>
      </div>
      <div v-if="passport.yarns?.length" class="yarn-list">
        <h4>{{ $t('engineering.structurePreview.yarnPalette') }}</h4>
        <article v-for="(yarn, index) in passport.yarns" :key="yarn.id ?? index">
          <span class="color-chip" :style="{ backgroundColor: yarn.color_code || '#dce6f0' }" aria-hidden="true"></span>
          <div><strong>{{ yarn.role || $t('engineering.passportDetail.yarn') }}</strong><span>{{ yarn.yarn_type || $t('engineering.passportDetail.unspecified') }} · {{ yarn.yarn_composition || yarn.color_name || $t('engineering.passportDetail.noComposition') }}</span></div>
        </article>
      </div>
      </div>
    </section>

    <div class="passport-layout">
      <section>
        <div class="section-heading">
          <h3>{{ $t('engineering.passportDetail.productionRoute') }}</h3>
          <span>{{ $t('engineering.passportDetail.steps', { count: passport.routes?.length ?? 0 }) }}</span>
        </div>
        <div v-if="!passport.routes?.length" class="empty-state">
          {{ $t('engineering.passportDetail.noRoute') }}
        </div>
        <table v-else>
          <thead>
            <tr><th>{{ $t('engineering.passportDetail.step') }}</th><th>{{ $t('engineering.passportDetail.operation') }}</th><th>{{ $t('engineering.passportDetail.workstation') }}</th><th>{{ $t('engineering.passportDetail.standardTime') }}</th><th>{{ $t('engineering.passportDetail.qc') }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="(step, index) in paginatedRoutes" :key="step.id ?? index">
              <td>{{ step.sort_order ?? (routePage - 1) * routePageSize + index + 1 }}</td>
              <td><strong>{{ step.operation_name || $t('engineering.passportDetail.unnamedOperation') }}</strong><span>{{ step.responsible_role || "-" }}</span></td>
              <td>{{ step.workstation_name || "-" }}</td>
              <td>{{ $t('engineering.passportDetail.minutes', { count: step.standard_time_minutes ?? 0 }) }}</td>
              <td>{{ step.qc_required ? $t('engineering.passportDetail.required') : $t('engineering.passportDetail.no') }}</td>
            </tr>
          </tbody>
        </table>
        <PaginationControls
          v-if="passport.routes?.length"
          v-model:page="routePage"
          v-model:page-size="routePageSize"
          :total="passport.routes.length"
        />
      </section>

      <section>
        <div class="section-heading">
          <h3>{{ $t('engineering.passportDetail.materialRecipe') }}</h3>
          <span>{{ $t('engineering.passportDetail.lines', { count: passport.raw_materials?.length ?? 0 }) }}</span>
        </div>
        <div v-if="!passport.raw_materials?.length" class="empty-state">
          {{ $t('engineering.passportDetail.noMaterials') }}
        </div>
        <table v-else>
          <thead>
            <tr><th>{{ $t('engineering.passportDetail.material') }}</th><th>{{ $t('engineering.passportDetail.stage') }}</th><th>{{ $t('engineering.passportDetail.perPair') }}</th><th>{{ $t('engineering.passportDetail.waste') }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="(material, index) in paginatedMaterials" :key="material.id ?? index">
              <td><strong>{{ material.material_name_snapshot || `Material #${material.material_product_id}` }}</strong><span>{{ material.color_name || material.thread_code || "-" }}</span></td>
              <td>{{ material.stage_code || "-" }}</td>
              <td>{{ material.consumption_per_pair ?? 0 }} {{ material.uom || "kg" }}</td>
              <td>{{ material.waste_percent ?? 0 }}%</td>
            </tr>
          </tbody>
        </table>
        <PaginationControls
          v-if="passport.raw_materials?.length"
          v-model:page="materialPage"
          v-model:page-size="materialPageSize"
          :total="passport.raw_materials.length"
        />
      </section>
    </div>

    <section>
      <div class="section-heading simulator-heading">
        <div>
          <h3>{{ $t('engineering.passportDetail.calculator') }}</h3>
          <p>{{ $t('engineering.passportDetail.calculatorHelp') }}</p>
        </div>
        <form class="quantity-form" @submit.prevent="calculateRequirements">
          <label for="pair-quantity">{{ $t('engineering.passportDetail.pairs') }}</label>
          <input
            id="pair-quantity"
            v-model.number="pairQuantity"
            type="number"
            min="1"
            step="1"
          >
          <button type="submit" :disabled="calculating">
            {{ calculating ? $t('engineering.passportDetail.calculating') : $t('engineering.passportDetail.calculate') }}
          </button>
        </form>
      </div>
      <div v-if="calculationError" class="inline-error" role="alert">
        {{ calculationError }}
      </div>
      <div v-else-if="!materialRequirements" class="empty-state calculator-empty">
        {{ $t('engineering.passportDetail.calculatorEmpty') }}
      </div>
      <div v-else-if="!materialRequirements.materials.length" class="empty-state calculator-empty">
        {{ $t('engineering.passportDetail.noRequirements') }}
      </div>
      <div v-else class="requirements-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('engineering.passportDetail.material') }}</th>
              <th>{{ $t('engineering.passportDetail.stage') }}</th>
              <th>{{ $t('engineering.passportDetail.perPair') }}</th>
              <th>{{ $t('engineering.passportDetail.waste') }}</th>
              <th>{{ $t('engineering.passportDetail.requiredFor', { count: materialRequirements.pairs }) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="material in paginatedRequirements"
              :key="`${material.stage_code}-${material.material_product_id}`"
            >
              <td>
                <strong>{{ material.material_name || `Material #${material.material_product_id}` }}</strong>
                <span>{{ material.color_name || material.thread_code || "-" }}</span>
              </td>
              <td>{{ material.stage_code || "-" }}</td>
              <td>{{ material.consumption_per_pair }} {{ material.uom || "" }}</td>
              <td>{{ material.waste_percent }}%</td>
              <td><strong>{{ material.required_quantity }} {{ material.uom || "" }}</strong></td>
            </tr>
          </tbody>
        </table>
        <PaginationControls
          v-model:page="requirementPage"
          v-model:page-size="requirementPageSize"
          :total="materialRequirements.materials.length"
        />
      </div>
    </section>

    <section>
      <div class="section-heading">
        <h3>{{ $t('engineering.passportDetail.revisionHistory') }}</h3>
        <span>{{ $t('engineering.passportDetail.changes', { count: passport.revisions?.length ?? 0 }) }}</span>
      </div>
      <div v-if="!passport.revisions?.length" class="empty-state">
        {{ $t('engineering.passportDetail.noRevisions') }}
      </div>
      <table v-else>
        <thead>
          <tr><th>{{ $t('engineering.passportDetail.revision') }}</th><th>{{ $t('engineering.passportDetail.date') }}</th><th>{{ $t('engineering.passportDetail.status') }}</th><th>{{ $t('engineering.passportDetail.changedBy') }}</th><th>{{ $t('engineering.passportDetail.description') }}</th></tr>
        </thead>
        <tbody>
          <tr v-for="(revision, index) in paginatedRevisions" :key="revision.id ?? index">
            <td>{{ revision.revision_no || revision.revision || `#${(revisionPage - 1) * revisionPageSize + index + 1}` }}</td>
            <td>{{ formatDisplayDate(revision.revision_date || revision.date) || "-" }}</td>
            <td>{{ revision.status ? $t(`engineering.passport.status.${revision.status}`) : "-" }}</td>
            <td>{{ revision.changed_by || revision.by || "-" }}</td>
            <td>{{ revision.change_description || revision.description || "-" }}</td>
          </tr>
        </tbody>
      </table>
      <PaginationControls
        v-if="passport.revisions?.length"
        v-model:page="revisionPage"
        v-model:page-size="revisionPageSize"
        :total="passport.revisions.length"
      />
    </section>
  </template>
</template>

<style scoped>
.back-link {
  display: inline-block;
  margin-bottom: 20px;
  color: #285b9a;
  font-size: 13px;
  font-weight: 700;
}

.page-heading {
  display: flex;
  gap: 24px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 5px;
  color: #66726e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 26px;
  letter-spacing: 0;
}

.page-heading p:last-child {
  margin: 7px 0 0;
  color: #66726e;
}

.status-stack {
  display: flex;
  gap: 7px;
}

.author-action {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 7px 10px;
  border: 1px solid #3568a8;
  border-radius: 8px;
  color: #fff;
  background: #3568a8;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.author-action--primary { border-color: #07996f; background: #07996f; }
.print-action { border-color: #173f70; background: #173f70; cursor: pointer; }
.print-document { display: none; }
.attachment-list__item {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid #d7dfdc;
  border-radius: 8px;
  background: #f8faf9;
}
.attachment-list__item strong {
  color: #173454;
  font-size: 13px;
}
.attachment-list {
  display: grid;
  gap: 10px;
  margin-top: 14px;
}
.attachment-list__item {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
}
.attachment-list__item p,
.attachment-list__item small {
  margin: 4px 0 0;
  color: #66726e;
}
.attachment-link {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #b7c3be;
  border-radius: 8px;
  color: #285b9a;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.print-columns--media {
  margin-top: 4mm;
}
.print-media-block {
  break-inside: avoid;
}
.print-attachment-list li {
  margin: 0;
  padding: 2mm;
  border: 1px solid #d1d5db;
}
.print-attachment-list {
  display: grid;
  gap: 2mm;
  margin: 0;
  padding: 0;
  list-style: none;
}
.print-attachment-list li {
  display: grid;
  gap: 1mm;
}
.print-attachment-list span,
.print-attachment-list small {
  color: #4b5563;
  font-size: 7pt;
}

.consistency-status {
  padding: 6px 9px;
  border: 1px solid;
  font-size: 12px;
  font-weight: 700;
}

.consistency-status--ok,
.consistency-ok {
  border-color: #86b4a4;
  color: #236451;
  background: #eff8f5;
}

.consistency-status--issue {
  border-color: #d2b37c;
  color: #765017;
  background: #fff8e9;
}

.consistency-ok {
  margin-top: 16px;
  padding: 14px;
  border-left: 3px solid #358267;
}

.consistency-list {
  display: grid;
  gap: 8px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}

.consistency-list li {
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  gap: 10px;
  padding: 11px;
  border: 1px solid;
  font-size: 13px;
}

.consistency-list--error {
  border-color: #d7a8a1 !important;
  color: #852d22;
  background: #fff5f3;
}

.consistency-list--warning {
  border-color: #ead6ad !important;
  color: #6c5734;
  background: #fffaf0;
}

.status-stack span {
  padding: 6px 8px;
  border: 1px solid #b7c3be;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
}

.readiness--ready {
  border-color: #86b4a4 !important;
  color: #236451;
  background: #eff8f5;
}

.readiness--blocked {
  border-color: #d2b37c !important;
  color: #765017;
  background: #fff8e9;
}

section {
  margin-bottom: 16px;
  padding: 22px;
  border: 1px solid #d7dfdc;
  background: #ffffff;
}

h3 {
  margin: 0 0 18px;
  font-size: 16px;
  letter-spacing: 0;
}

.definition-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin: 0;
}

.approval-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.rejection-banner {
  display: grid;
  gap: 4px;
  margin-bottom: 14px;
  padding: 14px 16px;
  border: 1px solid #e5b8b1;
  border-left: 4px solid #b0301e;
  background: #fff5f3;
}

.rejection-banner strong {
  color: #852d22;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.rejection-banner p {
  margin: 0;
  color: #6b3a33;
  line-height: 1.5;
}

.approval-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border: 1px solid #d7dfdc;
  border-radius: 8px;
  background: #f8fbfa;
}

.approval-card__head {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.approval-card__head > span {
  padding: 5px 8px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
}

.approval-card dl {
  display: grid;
  gap: 10px;
  margin: 0;
}

dt {
  color: #66726e;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

dd {
  margin: 5px 0 0;
}

.coverage-band {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  padding: 0;
}
.structure-section { display: grid; gap: 14px; padding: 0 16px 16px; overflow: hidden; border-color: #cbd8e5; background: #f7fafc; }
.structure-heading { grid-column: 1 / -1; padding: 22px 24px 18px; border-bottom: 1px solid #d9e3ec; background: #fff; }
.structure-heading h3 { margin-bottom: 5px; font-size: 19px; }
.section-kicker { margin: 0 0 5px!important; color: #3568a8!important; font-size: 10px!important; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; }
.structure-summary { display: flex; flex-wrap: wrap; gap: 7px; justify-content: flex-end; }
.structure-summary span { padding: 5px 9px; border: 1px solid #c7d5e3; border-radius: 999px; color: #425a70; background: #f4f8fb; font-size: 11px; font-weight: 700; }
.structure-main { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(290px, .65fr); gap: 14px; align-items: stretch; }
.structure-main > :deep(.drawing-preview) { min-height: 0; padding: 12px; }
.structure-main > :deep(.drawing-preview svg) { max-height: 280px; }
.dimension-panel { padding: 14px; border: 1px solid #d5e0e9; border-radius: 8px; background: #fff; }
.dimension-panel h4, .structure-list > h4, .yarn-list > h4 { margin: 0 0 13px; color: #173454; font-size: 13px; }
.structure-dimensions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 14px; margin: 0; }
.structure-dimensions div { display: flex; gap: 8px; align-items: center; justify-content: space-between; min-width: 0; padding: 8px 0; border-bottom: 1px solid #e7ecef; }
.structure-dimensions div:last-child { border-bottom: 0; }
.structure-dimensions dt { text-transform: none; }
.structure-dimensions dd { display: flex; gap: 4px; align-items: baseline; margin: 0; color: #173454; }
.structure-dimensions dd strong { font-size: 14px; font-variant-numeric: tabular-nums; }
.structure-dimensions dd span { color: #71807a; font-size: 10px; font-weight: 700; }
.structure-secondary { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(280px, .6fr); gap: 14px; align-items: start; }
.structure-list, .yarn-list { display: grid; gap: 7px; margin: 0; padding: 14px; border: 1px solid #d5e0e9; border-radius: 8px; background: #fff; }
.structure-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.structure-list > h4 { grid-column: 1 / -1; }
.yarn-list { grid-template-columns: 1fr; }
.yarn-list > h4 { grid-column: 1 / -1; }
.structure-list article { display: grid; grid-template-columns: 28px minmax(0, 1fr); gap: 8px; align-items: center; padding: 9px; border: 1px solid #e0e7eb; border-radius: 5px; background: #fbfcfd; }
.feature-index { display: grid; width: 26px; height: 26px; border-radius: 5px; color: #fff; background: #2d5f98; font-size: 10px; font-weight: 800; place-items: center; }
.feature-copy { display: grid; gap: 4px; min-width: 0; }
.feature-copy > div { display: flex; gap: 8px; align-items: center; }
.feature-copy strong { text-transform: capitalize; }
.feature-copy p, .feature-copy small { margin: 0; overflow: hidden; color: #66726e; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.feature-status--required, .feature-status--optional { padding: 2px 6px; border-radius: 999px; font-size: 9px; font-weight: 800; text-transform: uppercase; }
.feature-status--required { color: #17634d!important; background: #e5f5ef; }
.feature-status--optional { color: #6b5a25!important; background: #fbf3d9; }
.feature-size { display: flex; grid-column: 2; gap: 4px; align-items: baseline; justify-items: start; color: #173454; font-size: 11px; font-variant-numeric: tabular-nums; }
.feature-size span { color: #71807a; font-size: 9px; font-weight: 700; }
.yarn-list article { display: grid; grid-template-columns: 28px minmax(0, 1fr); gap: 9px; align-items: center; padding: 9px; border: 1px solid #e0e7eb; border-radius: 5px; background: #fbfcfd; }
.yarn-list article div { display: grid; gap: 2px; min-width: 0; }
.yarn-list article div > span, .yarn-list small { color: #66726e; font-size: 11px; }
.yarn-list code { color: #53625d; font-size: 10px; }
.color-chip { width: 24px; height: 24px; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 1px #b9c5c1; }

.coverage-band div {
  display: grid;
  gap: 3px;
  padding: 16px;
  border-right: 1px solid #e1e6e4;
}

.coverage-band div:last-child {
  border-right: 0;
}

.coverage-band strong {
  font-size: 22px;
}

.coverage-band span {
  color: #66726e;
  font-size: 11px;
}

.passport-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.section-heading {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.section-heading h3 {
  margin-bottom: 18px;
}

.section-heading span {
  color: #66726e;
  font-size: 12px;
}

.section-heading p {
  margin: -12px 0 0;
  color: #66726e;
  font-size: 12px;
}

.passport-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.passport-tabs a {
  padding: 7px 9px;
  border: 1px solid #b7c3be;
  border-radius: 8px;
  color: #285b9a;
  font-size: 12px;
  text-decoration: none;
  text-transform: capitalize;
}

.passport-tabs a.active {
  border-color: #3568a8;
  color: #ffffff;
  background: #3568a8;
}

.simulator-heading {
  align-items: flex-end;
}

.quantity-form {
  display: grid;
  grid-template-columns: auto 110px auto;
  gap: 8px;
  align-items: center;
}

.quantity-form label {
  color: #586660;
  font-size: 12px;
  font-weight: 700;
}

.quantity-form input {
  min-width: 0;
  padding: 8px;
  border: 1px solid #b7c3be;
}

.quantity-form button {
  padding: 8px 12px;
}

.quantity-form button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.inline-error {
  margin-top: 14px;
  padding: 11px;
  border: 1px solid #d7a8a1;
  color: #852d22;
  background: #fff5f3;
}

.calculator-empty {
  min-height: 90px;
}

.requirements-table {
  margin-top: 16px;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

th,
td {
  padding: 9px;
  border: 1px solid #e1e6e4;
  text-align: left;
  vertical-align: top;
}

th {
  color: #586660;
  background: #f3f6f5;
  font-size: 10px;
  text-transform: uppercase;
}

td span {
  display: block;
  margin-top: 3px;
  color: #71807a;
  font-size: 10px;
}

.empty-state,
.page-state {
  display: grid;
  min-height: 150px;
  color: #697671;
  place-items: center;
}

.page-state {
  min-height: 240px;
}

.page-state--error {
  align-content: center;
  gap: 12px;
  color: #852d22;
}

@media (max-width: 980px) {
  .passport-layout {
    grid-template-columns: 1fr;
  }

  .coverage-band {
    grid-template-columns: repeat(3, 1fr);
  }

  .structure-main, .structure-secondary { grid-template-columns: 1fr; }
}

@media (max-width: 680px) {
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .definition-grid {
    grid-template-columns: 1fr 1fr;
  }

  .coverage-band {
    grid-template-columns: repeat(2, 1fr);
  }

  .attachment-list__item {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .simulator-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .quantity-form {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .structure-heading { align-items: flex-start; flex-direction: column; }
  .structure-summary { justify-content: flex-start; }
  .structure-section { padding: 0 10px 10px; }
  .structure-list { grid-template-columns: 1fr; }
  .structure-list article { grid-template-columns: 28px minmax(0, 1fr); }
  .structure-dimensions { grid-template-columns: 1fr; }
  .yarn-list { grid-template-columns: 1fr; }
}

@media print {
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  :global(.app-sidebar),
  :global(.page-toolbar) {
    display: none !important;
  }

  :global(.app-frame),
  :global(.app-main),
  :global(.app-content) {
    display: block !important;
    width: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .page-heading,
  .section-error,
  section:not(.print-document),
  .passport-layout {
    display: none !important;
  }

  .print-document {
    display: block !important;
    margin: 0;
    padding: 0;
    border: 0;
    color: #111827;
    font-family: Arial, sans-serif;
  }

  .print-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 72mm;
    gap: 8mm;
    align-items: start;
    padding-bottom: 4mm;
    border-bottom: 1.5px solid #173f70;
  }

  .print-header p {
    margin: 0 0 2mm;
    color: #4b5563;
    font-size: 8pt;
    font-weight: 700;
    text-transform: uppercase;
  }

  .print-header h1 {
    margin: 0 0 2mm;
    font-size: 18pt;
  }

  .print-header > div > strong {
    font-size: 11pt;
  }

  .print-header dl {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0;
    border: 1px solid #9ca3af;
  }

  .print-header dl div {
    padding: 2mm;
    border-right: 1px solid #d1d5db;
    border-bottom: 1px solid #d1d5db;
  }

  .print-header dt { font-size: 6.5pt; }
  .print-header dd { margin-top: 1mm; font-size: 8.5pt; font-weight: 700; text-transform: capitalize; }

  .print-definition {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin: 4mm 0;
    border: 1px solid #d1d5db;
  }

  .print-definition div {
    display: grid;
    gap: 1mm;
    padding: 2mm;
    border-right: 1px solid #d1d5db;
    border-bottom: 1px solid #d1d5db;
  }

  .print-definition span { color: #4b5563; font-size: 6.5pt; text-transform: uppercase; }
  .print-definition strong { font-size: 8.5pt; }
  .print-document :deep(.drawing-preview) { margin: 3mm 0; padding: 2mm; break-inside: avoid; }
  .print-document :deep(.drawing-preview svg) { width: 92mm; max-height: 75mm; }
  .print-document :deep(.drawing-preview__heading),
  .print-document :deep(figcaption) { display: none; }

  .print-document h2 {
    margin: 4mm 0 1.5mm;
    padding-bottom: 1mm;
    border-bottom: 1px solid #9ca3af;
    font-size: 9pt;
    text-transform: uppercase;
  }

  .print-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4mm;
    break-inside: avoid;
  }

  .print-columns--media {
    grid-template-columns: 1fr;
  }

  .print-document table {
    break-inside: auto;
    font-size: 7pt;
  }

  .print-document tr { break-inside: avoid; }
  .print-document th,
  .print-document td { padding: 1.5mm; border-color: #9ca3af; }
  .print-document th { color: #111827; background: #eef2f7 !important; font-size: 6.5pt; }

  .print-approvals {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5mm;
    margin-top: 8mm;
    break-inside: avoid;
  }

  .print-approvals div {
    display: grid;
    min-height: 24mm;
    padding: 2mm;
    border: 1px solid #9ca3af;
  }

  .print-approvals span { color: #4b5563; font-size: 7pt; text-transform: uppercase; }
  .print-approvals strong { align-self: center; font-size: 9pt; }
  .print-approvals i { align-self: end; padding-top: 2mm; border-top: 1px solid #d1d5db; color: #6b7280; font-size: 6.5pt; font-style: normal; }
}
</style>
